import React, { useEffect } from 'react';

import type { CategoryDataItem, DeliverableDataItem, ProductDataItem } from 'pages/access_codes/components/AssignContent/AssignContent';
import { DeliverableContentTreeView } from 'components/DeliverableContentTreeView/DeliverableContentTreeView';
import { ContentDeliverable, CourseGating, TransactionApi, TransactionDetails } from 'apis/TransactionApi';
import { TransactionDetailsContext, useTransactionDetails } from 'pages/access_codes/context';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FindContent } from 'components/FindContent/FindContent';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { compareDays, formatDay } from 'utils';
import { useSuperState } from 'hooks';
import moment from 'moment';

interface DefaultSettingsForm {
    booksPerStudent: number;
    courseGateType: any;
    coursesPerCode: number;
    enableBulb: boolean;
    enableCloud: boolean;
    enableQuestionReview: boolean;
    enableStudyGuide: boolean;
    endDate: Date | null;
    finalRedemptionDate: Date;
    startDate: Date | undefined;
    testMode: boolean;
    testsPerStudent: number;
    trainingMode: boolean;
    usageDays: number | undefined;
}

const treeViewDataManipulation = (content: void | ContentDeliverable[]): Promise<CategoryDataItem[]> =>

    // This is messy I'm sorry, just tryna get a treeview to work.
    // eslint-disable-next-line implicit-arrow-linebreak
    new Promise((resolve, reject) => {

        if (content && content.length > 0) {

            const treeviewData: any[] = [];
            const initiallyExpanded = content.length <= 500;
            for (let i = 0; i < content.length; i++) {

                const deliverableItem: ContentDeliverable = content[i];
                const categoryIndex = treeviewData.findIndex(
                    (item: any) => item.categoryId === deliverableItem.CategoryId || item.text === deliverableItem.CategoryName
                );
                const productIndex = treeviewData[categoryIndex]?.items.findIndex(
                    (item: any) => item.productId === deliverableItem.ProductId
                );
                if (categoryIndex === -1) {

                    treeviewData.push({
                        categoryId: deliverableItem.CategoryId,
                        deliverableTypeId: deliverableItem.DeliverableTypeId,
                        expanded: initiallyExpanded,
                        items: [
                            {
                                deliverableTypeId: deliverableItem.DeliverableTypeId,
                                expanded: initiallyExpanded,
                                items: [
                                    {
                                        ...deliverableItem,
                                        expanded: initiallyExpanded,
                                        key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                                        text: deliverableItem.Name
                                    }
                                ],
                                key: `${deliverableItem.ProductId}-${deliverableItem.DeliverableTypeId}`,
                                productId: deliverableItem.ProductId,
                                text: deliverableItem.ProductName
                            }
                        ],
                        key: `${deliverableItem.CategoryId}-${deliverableItem.DeliverableTypeId}`,
                        text: deliverableItem.CategoryName
                    });

                }

                if (productIndex === -1) {

                    treeviewData[categoryIndex].items.push({
                        deliverableTypeId: deliverableItem.DeliverableTypeId,
                        expanded: initiallyExpanded,
                        items: [
                            {
                                ...deliverableItem,
                                expanded: initiallyExpanded,
                                key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                                text: deliverableItem.Name
                            }
                        ],
                        key: `${deliverableItem.ProductId}-${deliverableItem.DeliverableTypeId}`,
                        productId: deliverableItem.ProductId,
                        text: deliverableItem.ProductName
                    });

                } else if (treeviewData.length > 0) {

                    treeviewData[categoryIndex]?.items[productIndex].items.push({
                        ...deliverableItem,
                        expanded: initiallyExpanded,
                        key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                        text: deliverableItem.Name
                    });

                }

            }
            resolve(treeviewData);

        } else {

            resolve([]);

        }

    });

export const AccessCodesContent = props => {

    const [state, setState] = useSuperState({
        courseGatingTypes: [] as CourseGating[],
        defaultSettingsForm: {
            booksPerStudent: 0,
            courseGateType: undefined,
            coursesPerCode: 0,
            enableBulb: false,
            enableCloud: false,
            enableQuestionReview: false,
            enableStudyGuide: false,
            endDate: null,
            finalRedemptionDate: new Date(),
            startDate: new Date(),
            testMode: false,
            testsPerStudent: 0,
            trainingMode: false,
            usageDays: undefined
        } as DefaultSettingsForm,
        error: null as string,
        loading: false,
        search: '',
        showContentModal: false,
        showDefaultEditModal: false,
        treeViewData: [] as CategoryDataItem[]
    });
    const { courseGatingTypes, defaultSettingsForm, error, loading, search, showContentModal, showDefaultEditModal, treeViewData } = state;

    const intl = useIntl();
    const { formatMessage } = intl;

    const { pageContext, updateById } = useTransactionDetails();
    const selectedTabData = pageContext?.Data;
    const transactionDetails = selectedTabData?.transactionDetails;
    const mostRestrictiveLicense = selectedTabData?.mostRestrictiveLicense?.license;
    const licenses = selectedTabData?.licenses ?? [];

    const { createToast } = useNotification();

    const clearError = () => setState({ error: null });

    const setDefaultSettingsForm = (defaultSettingsForm: DefaultSettingsForm) => setState({ defaultSettingsForm });

    const updateDefaultSettingsForm = (updates: Partial<DefaultSettingsForm>) => setDefaultSettingsForm({ ...state.defaultSettingsForm, ...updates });

    const parsePickerDate = (value: unknown): Date | null | undefined => {

        if (value === undefined) {

            return undefined;

        }

        if (value === null || value === '') {

            return null;

        }

        if (value instanceof Date) {

            return Number.isNaN(value.getTime()) ? null : value;

        }

        const splitValue = String(value).split('-');
        if (splitValue.length < 3) {

            return null;

        }

        const parsedDate = moment(value as any).toDate();
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;

    };

    const parseNumberValue = (value: string, fallback = 0, max = Number.MAX_SAFE_INTEGER) => {

        const parsedNumber = parseInt(value, 10);
        if (Number.isNaN(parsedNumber)) {

            return fallback;

        }

        if (parsedNumber < 0) {

            return 0;

        }

        if (parsedNumber > max) {

            return max;

        }

        return parsedNumber;

    };

    const getCourseGatingTypes = async () => {

        try {

            const courseGatingTypes = await TransactionApi.GetCourseGatingTypes(1);
            setState({ courseGatingTypes });

        } catch (e) {

            console.error(e);

        }

    };

    const isUnlimitedMax = (value: any) => {

        if (value === -1 || value === undefined || value === null) {

            return 9999;

        } else {

            return value;

        }

    };

    const hierarchyFilter = (data: any): CategoryDataItem[] => {

        const hierarchySearchedData: CategoryDataItem[] = [];

        data.map((categoryItem: CategoryDataItem) => categoryItem.items.map((productItem: ProductDataItem) => productItem.items.map((dataItem: DeliverableDataItem) => {

            if (dataItem.text && dataItem.text.toLowerCase().includes(search.toLowerCase())) {

                let categoryIndex = hierarchySearchedData.findIndex(
                    (category: CategoryDataItem) => category.categoryId === categoryItem.categoryId
                );
                let productIndex = -1;
                if (categoryIndex === -1) {

                    hierarchySearchedData.push({ ...categoryItem, items: [] });
                    categoryIndex = hierarchySearchedData.findIndex(
                        (category: CategoryDataItem) => category.categoryId === categoryItem.categoryId
                    );

                } else {

                    productIndex = hierarchySearchedData[categoryIndex].items.findIndex(
                        (product: ProductDataItem) => product.productId === productItem.productId
                    );

                }
                if (productIndex === -1) {

                    hierarchySearchedData[categoryIndex].items.push({ ...productItem, items: [] });
                    productIndex = hierarchySearchedData[categoryIndex].items.findIndex(
                        (product: ProductDataItem) => product.productId === productItem.productId
                    );

                }
                {

                    hierarchySearchedData[categoryIndex].items[productIndex].items.push(dataItem);

                }

            }

        })));

        return hierarchySearchedData;

    };

    const handleSaveDefaultSettings = async () => {

        try {

            //validation should go here
            if (!transactionDetails) return;

            setState({ loading: true });

            const defaultSettingsForm = state.defaultSettingsForm;

            const result = await TransactionApi.Update({
                ...transactionDetails,
                AllowReview: defaultSettingsForm.enableQuestionReview,

                AllowStudyGuide: defaultSettingsForm.enableStudyGuide,
                CourseGateType: defaultSettingsForm.courseGateType,

                // UsageDays: defaultSettingsForm.usageDays,
                ExpirationDate: defaultSettingsForm.endDate,
                HasBulbAccess: defaultSettingsForm.enableBulb,
                HasCloud: defaultSettingsForm.enableCloud,
                LeastOrderExpirationDate: defaultSettingsForm.finalRedemptionDate,
                NumOfBooks: defaultSettingsForm.booksPerStudent,
                NumOfCourses: defaultSettingsForm.coursesPerCode,
                NumOfTests: defaultSettingsForm.testsPerStudent,
                StartDate: defaultSettingsForm.startDate,
                TestingMode: defaultSettingsForm.testMode,
                TrainingMode: defaultSettingsForm.trainingMode
            } as TransactionDetails);

            if (!result.Done) throw new Error(result.Message);

            // Keep each student's per-record expiration in sync with the default when it changes,
            // so the edit dialogs on the student pages reflect the new end date.
            const previousEndDate = transactionDetails.ExpirationDate ?? null;
            const newEndDate = defaultSettingsForm.endDate ?? null;
            const endDateChanged = !!previousEndDate !== !!newEndDate
                || (!!previousEndDate && !!newEndDate && compareDays(previousEndDate, newEndDate) !== 0);
            if (endDateChanged && newEndDate) {

                await TransactionApi.UpdateStudentExpirationByTransaction(transactionDetails.Id, newEndDate);

            }

            createToast(formatMessage({ id: 'the_content_was_saved_successfully.6c899203', defaultMessage: 'The content was saved successfully' }), 'success');

            updateById(pageContext?.Id ?? 0, {
                transactionDetails: {
                    ...transactionDetails,
                    AllowReview: defaultSettingsForm.enableQuestionReview,
                    AllowStudyGuide: defaultSettingsForm.enableStudyGuide,
                    CourseGateType: defaultSettingsForm.courseGateType,
                    ExpirationDate: defaultSettingsForm.endDate,
                    HasBulbAccess: defaultSettingsForm.enableBulb,
                    HasCloud: defaultSettingsForm.enableCloud,
                    LeastOrderExpirationDate: defaultSettingsForm.finalRedemptionDate,
                    NumOfBooks: defaultSettingsForm.booksPerStudent,
                    NumOfCourses: defaultSettingsForm.coursesPerCode,
                    NumOfTests: defaultSettingsForm.testsPerStudent,
                    StartDate: defaultSettingsForm.startDate,
                    TestingMode: defaultSettingsForm.testMode,
                    TrainingMode: defaultSettingsForm.trainingMode,
                    UsageDays: defaultSettingsForm.usageDays
                } as TransactionDetails
            });

            setState({
                loading: false,
                showDefaultEditModal: false
            });

        } catch (e) {

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    useEffect(() => {

        if (!transactionDetails) return;

        const defaultSettings = transactionDetails;
        setDefaultSettingsForm({
            booksPerStudent: defaultSettings.NumOfBooks ?? 0,
            courseGateType: defaultSettings.CourseGateType ?? 0,
            coursesPerCode: defaultSettings.NumOfCourses ?? 0,
            enableBulb: defaultSettings.HasBulbAccess ?? false,
            enableCloud: defaultSettings.HasCloud ?? false,
            enableQuestionReview: defaultSettings.AllowReview ?? false,
            enableStudyGuide: defaultSettings.AllowStudyGuide ?? false,
            endDate: defaultSettings.ExpirationDate,
            finalRedemptionDate: defaultSettings.LeastOrderExpirationDate ?? new Date(),
            startDate: defaultSettings.StartDate,
            testMode: defaultSettings.TestingMode,
            testsPerStudent: defaultSettings.NumOfTests ?? 0,
            trainingMode: defaultSettings.TrainingMode,
            usageDays: defaultSettings.UsageDays ?? mostRestrictiveLicense?.UsageDays
        });

    }, [transactionDetails, mostRestrictiveLicense, showDefaultEditModal]);

    useEffect(() => {

        getCourseGatingTypes();

    }, []);

    useEffect(() => {

        if (!selectedTabData?.content) return;

        (async () => {

            try {

                const treeViewData = await treeViewDataManipulation(selectedTabData.content);
                setState({ treeViewData });

            } catch (e) {

                console.error(e);

            }

        })();

    }, [selectedTabData?.content]);

    return (
        <>
            <GMetrixDialog
                actionButtonClick={handleSaveDefaultSettings}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                dialogLabel={formatMessage({ id: 'edit_default_settings.cf08e662', defaultMessage: 'Edit Default Settings' })}
                disabled={loading}
                onCancel={() => setState({ loading: false, showDefaultEditModal: false })}
                show={showDefaultEditModal}
                size="xl"
            >

                <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                    {error}
                </Alert>

                <Form>
                    {/* Default Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Default Settings" id="default_settings.e49d564c" />
                    </h5>

                    {/* this will be used once it is ready proc side */}
                    {/* <Row className="pb-3 pt-2 mb-2 rounded" style={{ backgroundColor: "#F2F5FA" }}>
                        <Col md={6}>
                            <GMetrixInput type="number" value={defaultSettingsForm.usageDays} label="Usage Days" infoTitle="Access to content will expire based on whichever is earlierL 'Usage Days' or the 'Final Usage Date'." onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, usageDays: parseInt(e.target.value) })}/>

                        </Col>
                        <Col md={6}>
                            <GMetrixDatePicker value={defaultSettingsForm.endDate} label="Final Usage Date" onChange={e => {

                                const dateArray = (e as unknown as string).split("-");
                                const endDate = new Date(`${dateArray[0]}/${dateArray[1]}/${dateArray[2]}`);
                                setDefaultSettingsForm({ ...defaultSettingsForm, endDate: new Date(endDate) });

                            }}/>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixDatePicker value={defaultSettingsForm.finalRedemptionDate} label="Final Redemption Date" infoTitle="This is the last date that code(s) can be redeemed for access to content" onChange={e => {

                                const dateArray = (e as unknown as string).split("-");
                                const finalEndDate = new Date(`${dateArray[0]}/${dateArray[1]}/${dateArray[2]}`);
                                setDefaultSettingsForm({ ...defaultSettingsForm, finalRedemptionDate: new Date(finalEndDate) });

                            }}/>
                        </Col>
                    </Row> */}

                    <Row className="pb-3 pt-2 mb-2 rounded" style={{ backgroundColor: '#F2F5FA' }}>
                        <Col md={6}>
                            <GMetrixDatePicker
                                onChange={e => {

                                    const parsedStartDate = parsePickerDate(e);
                                    if (parsedStartDate === undefined) {

                                        updateDefaultSettingsForm({ startDate: undefined });
                                        return;

                                    }

                                    updateDefaultSettingsForm({ startDate: parsedStartDate ? new Date(parsedStartDate) : undefined });

                                }}
                                label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                readOnly={loading}
                                value={defaultSettingsForm.startDate}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDatePicker
                                onChange={e => {

                                    const parsedEndDate = parsePickerDate(e);
                                    if (parsedEndDate === undefined || parsedEndDate === null) {

                                        updateDefaultSettingsForm({ endDate: null });
                                        return;

                                    }

                                    const endDate = new Date(parsedEndDate);
                                    if (defaultSettingsForm.startDate && compareDays(defaultSettingsForm.startDate, endDate) > 0) {

                                        createToast(formatMessage({ id: 'you_cannot_set_the_end_date_before_the_s.e431eb11', defaultMessage: 'You cannot set the end date before the start date.' }), 'danger');
                                        return;

                                    }
                                    const licenseExpireDate = mostRestrictiveLicense?.OrderExpireDate as unknown as string | undefined;

                                    // Day-precision compare: the license expiration day itself is allowed.
                                    if (licenseExpireDate && compareDays(endDate, licenseExpireDate) > 0) {

                                        createToast(formatMessage({ id: 'you_cannot_set_the_end_date_after_your_l.7bbc8d74', defaultMessage: 'You cannot set the end date after your license expiration date of {date}.' }, { date: formatDay(licenseExpireDate, intl) }), 'danger');
                                        return;

                                    }
                                    updateDefaultSettingsForm({ endDate: new Date(endDate) });

                                }}
                                label={formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })}
                                readOnly={loading}
                                value={defaultSettingsForm.endDate}
                            />
                        </Col>
                    </Row>

                    <div className="d-flex">
                        <GMetrixCheckInput
                            disabled
                            checked={defaultSettingsForm.enableBulb}
                            infoTitle={formatMessage({ id: 'enables_bulb_access.67a4b911', defaultMessage: 'Enables bulb access' })}
                            label={formatMessage({ id: 'enable_bulb_portfolio.a0c8d39b', defaultMessage: 'Enable bulb Portfolio' })}
                            type="checkbox"
                        />
                        {licenses.some(license => license.CloudAccessUses > 0) && (
                            <GMetrixCheckInput
                                checked={defaultSettingsForm.enableCloud}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'enables_cloud_access.1d170166', defaultMessage: 'Enables cloud access' })}
                                label={formatMessage({ id: 'cloud_access.95fe7c27', defaultMessage: 'Cloud Access' })}
                                onChange={e => updateDefaultSettingsForm({ enableCloud: e.target.checked })}
                                type="checkbox"
                            />
                        )}

                    </div>

                    <hr />

                    {/* Test Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Test Settings" id="test_settings.dfedf589" />
                    </h5>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsedValue = parseNumberValue(e.target.value, defaultSettingsForm.testsPerStudent, isUnlimitedMax(mostRestrictiveLicense?.LicensePracticeTestsNumUses));
                                    updateDefaultSettingsForm({ testsPerStudent: parsedValue });

                                }}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_test_attempts_that_a.97601508', defaultMessage: 'Limit the number of Test Attempts that a student can take across all Tests in this Access Code' })}
                                label={formatMessage({ id: 'tests_per_student.44f8c927', defaultMessage: 'Tests per Student' })}
                                max={isUnlimitedMax(mostRestrictiveLicense?.LicensePracticeTestsNumUses)}
                                subLabel={`(max ${isUnlimitedMax(mostRestrictiveLicense?.LicensePracticeTestsNumUses)})`}
                                type="number"
                                value={defaultSettingsForm.testsPerStudent}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                onChange={mode => {

                                    const selectedMode = Array.isArray(mode) ? mode[0] : mode;
                                    if (!selectedMode) {

                                        return;

                                    }

                                    switch (selectedMode.name) {

                                        case 'Training':
                                            updateDefaultSettingsForm({ testMode: false, trainingMode: true });
                                            break;
                                        case 'Testing':
                                            updateDefaultSettingsForm({ testMode: true, trainingMode: false });
                                            break;
                                        case 'Both':
                                        default:
                                            updateDefaultSettingsForm({ testMode: true, trainingMode: true });
                                            break;

                                    }

                                }}
                                dataItemKey="id"
                                dataItems={[{ id: 1, name: 'Training' }, { id: 2, name: 'Testing' }, { id: 3, name: 'Both' }]}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'training_unlimited_time_with_detailed_fe.cbfaabb1', defaultMessage: 'Training - Unlimited time with detailed feedback Testing - Timed with no detailed feedback' })}
                                label={formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}
                                textField="name"
                                value={defaultSettingsForm.trainingMode && defaultSettingsForm.testMode ? { id: 3, name: 'Both' } : defaultSettingsForm.trainingMode ? { id: 1, name: 'Training' } : { id: 2, name: 'Testing' }}
                            />
                        </Col>
                    </Row>

                    <div className="d-flex">
                        <GMetrixCheckInput
                            checked={defaultSettingsForm.enableQuestionReview}
                            disabled={loading}
                            infoTitle={formatMessage({ id: 'permit_students_to_review_missed_questio.bdd3f3df', defaultMessage: 'Permit students to review missed questions without changing the score in Testing Mode.' })}
                            label={formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })}
                            onChange={e => updateDefaultSettingsForm({ enableQuestionReview: e.target.checked })}
                            type="checkbox"
                        />
                        <GMetrixCheckInput
                            checked={defaultSettingsForm.enableStudyGuide}
                            disabled={loading}
                            infoTitle={formatMessage({ id: 'permit_access_to_the_gmetrix_study_guide.ad613c7b', defaultMessage: 'Permit access to the Gmetrix Study Guides when available.' })}
                            label={formatMessage({ id: 'study_guide.8c412365', defaultMessage: 'Study Guide' })}
                            onChange={e => updateDefaultSettingsForm({ enableStudyGuide: e.target.checked })}
                            type="checkbox"
                        />
                    </div>

                    <hr />

                    {/* Course Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Course Settings" id="course_settings.4f8c306c" />
                    </h5>

                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsedValue = parseNumberValue(e.target.value, defaultSettingsForm.coursesPerCode, isUnlimitedMax(mostRestrictiveLicense?.LicenseCoursesMaxUses));
                                    updateDefaultSettingsForm({ coursesPerCode: parsedValue });

                                }}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_courses_that_a_stude.fad9a74b', defaultMessage: 'Limit the number of Courses that a student can take across all Courses in this Access Code' })}
                                label={formatMessage({ id: 'courses_per_code.139bd3bd', defaultMessage: 'Courses Per Code' })}
                                max={isUnlimitedMax(mostRestrictiveLicense?.LicenseCoursesMaxUses)}
                                subLabel={`(max ${isUnlimitedMax(mostRestrictiveLicense?.LicenseCoursesMaxUses)})`}
                                type="number"
                                value={defaultSettingsForm.coursesPerCode}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                onChange={type => {

                                    const selectedType = Array.isArray(type) ? type[0] : type;
                                    if (!selectedType) {

                                        return;

                                    }

                                    updateDefaultSettingsForm({ courseGateType: selectedType.Id });

                                }}
                                dataItemKey="Id"
                                dataItems={courseGatingTypes}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'students_must_meet_the_selected_criteria.d4d34c5c', defaultMessage: 'Students must meet the selected criteria to progress through the course' })}
                                label={formatMessage({ id: 'course_gating.10e89741', defaultMessage: 'Course Gating' })}
                                textField="TranslatedName"
                                value={courseGatingTypes[defaultSettingsForm.courseGateType ?? 0]}
                            >
                            </GMetrixDropdown>

                        </Col>
                    </Row>

                    <hr />

                    {/* Book Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Book Settings" id="book_settings.ae00f5eb" />
                    </h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const parsedValue = parseNumberValue(e.target.value, defaultSettingsForm.booksPerStudent, isUnlimitedMax(mostRestrictiveLicense?.LicenseEbookMaxUses));
                                    updateDefaultSettingsForm({ booksPerStudent: parsedValue });

                                }}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_books_that_a_student.09a305a1', defaultMessage: 'Limit the number of Books that a student can read across all Books in this Access Code' })}
                                label={formatMessage({ id: 'books_per_student.36f8ef23', defaultMessage: 'Books Per Student' })}
                                max={isUnlimitedMax(mostRestrictiveLicense?.LicenseEbookMaxUses)}
                                subLabel={`(max ${isUnlimitedMax(mostRestrictiveLicense?.LicenseEbookMaxUses)})`}
                                type="number"
                                value={defaultSettingsForm.booksPerStudent}
                            />
                        </Col>
                    </Row>
                </Form>
            </GMetrixDialog>

            <FindContent
                currentContext={TransactionDetailsContext}
                onCancel={() => setState({ showContentModal: false })}
                onSuccess={() => setState({ showContentModal: false })}
                show={showContentModal}
                variant="dialog"
            />
            <Row className="mb-3">
                <Col md={4}>
                    <GMetrixInput
                        disabled={loading}
                        onChange={e => setState({ search: e.target.value })}
                        placeholder={formatMessage({ id: 'search_courses_tests_or_assignments.b8c4464f', defaultMessage: 'Search courses, tests, or assignments...' })}
                        type="text"
                        value={search}
                    />
                </Col>
                <Col className="text-end" md={8}>
                    <GMetrixButton
                        className="me-2"
                        disabled={loading}
                        iconClass="fa-regular fa-cog"
                        onClick={() => setState({ showDefaultEditModal: true })}
                        variant="outline-primary"
                    >
                        <FormattedMessage defaultMessage="Default Settings" id="default_settings.e49d564c" />
                    </GMetrixButton>
                    <GMetrixButton
                        disabled={loading}
                        iconClass="fa-regular fa-plus"
                        onClick={() => setState({ showContentModal: true })}
                        variant="primary"
                    >
                        <FormattedMessage defaultMessage="Add Content" id="add_content.aad4180d" />
                    </GMetrixButton>
                </Col>
            </Row>

            {treeViewData.length > 0 && selectedTabData?.content ?
                (
                    <DeliverableContentTreeView
                        courseGatingTypes={courseGatingTypes}
                        currentContext={TransactionDetailsContext}
                        data={hierarchyFilter(treeViewData) ?? []}
                        totalListItemCount={selectedTabData.content.length}
                    />
                )
                :
                (
                    <div className="py-3 text-center content-settings__no-resources">
                        <FormattedMessage defaultMessage="No content has been added." id="no_content_has_been_added.9e012e26" />
                    </div>
                )
            }

        </>
    );

};
